import { Button, Space } from "antd";
import { useTranslation } from "react-i18next";
import { TrackOnClick } from "../../host/TrackOnClick";

export interface ActionButtonsProps {
  /**
   * AHA-43204: owner-only (not owner-or-admin) so only the team owner sees
   * the Buy licences / Upgrade team button — both flows are billing actions
   * and admins aren't authorised to act on them. The Invite button below
   * stays gated on canInvite (SCIM) alone.
   */
  currentUserIsTeamOwner: boolean;
  isPaidTeamPlan: boolean;
  /**
   * AHA-43204: whether the account record has arrived from the API
   * (`useAccount` is a separate fetch from `useProfile`). Until it has,
   * `isPaidTeamPlan` defaults to `false`, which would paint "Upgrade
   * team plan" first and then flip to "Buy licences" the moment the
   * account loads — a visible label flash. Hide the upgrade button
   * entirely during that window; the Invite button still renders.
   */
  isAccountLoaded: boolean;
  isCreatingCustomerPortal: boolean;
  scimEnabled: boolean;
  /**
   * AHA-43040: Unleash override (`AHA-42627-scim-manual-management`) that
   * lets SCIM-provisioned teams (ATB et al) still see manual-management
   * controls. Combine with `scimEnabled` at every gate.
   */
  canUseScimManualManagement: boolean;
  onUpgrade: () => void;
  onInvite: () => void;
}

// `name` values mirror TeamManagementV2ActionButtons.vue's
// `v-aha-emit-action :name="..."` so Mixpanel sees identical event
// names (`click_invite_member_button`, `click_upgrade_team_button` /
// `click_buy_licenses_button`).
export function ActionButtons({
  currentUserIsTeamOwner,
  isPaidTeamPlan,
  isAccountLoaded,
  isCreatingCustomerPortal,
  scimEnabled,
  canUseScimManualManagement,
  onUpgrade,
  onInvite,
}: ActionButtonsProps) {
  const { t } = useTranslation();
  const canInvite = !scimEnabled || canUseScimManualManagement;

  // AHA2-21: sizing cloned exactly from the presenter reference
  // (TeamManagementV2ActionButtons.vue @ acc3d51). The action-button container
  // is `flex items-center gap-3` → 12px between the two buttons, and each
  // button is pinned to `!h-10 !min-h-0` → 40px height (min-height 0 so the
  // design-system min never inflates it). Matching 40px lets these buttons
  // centre level with the 40px-tall licence-meter block beside them.
  const buttonStyle: React.CSSProperties = { height: 40, minHeight: 0 };

  return (
    <Space size={12}>
      {currentUserIsTeamOwner && isAccountLoaded && (
        <TrackOnClick name={isPaidTeamPlan ? "buy-licenses-button" : "upgrade-team-button"}>
          <Button
            onClick={onUpgrade}
            loading={isCreatingCustomerPortal}
            style={buttonStyle}
            data-testid="my-team-upgrade-team-button"
          >
            {isPaidTeamPlan ? t("team.action.buyLicenses") : t("team.action.upgrade")}
          </Button>
        </TrackOnClick>
      )}
      {canInvite && (
        <TrackOnClick name="invite-member-button">
          <Button
            type="primary"
            onClick={onInvite}
            style={buttonStyle}
            data-testid="my-team-invite-member-button"
          >
            {t("team.action.invite")}
          </Button>
        </TrackOnClick>
      )}
    </Space>
  );
}
